import React from 'react';

type Level = 1 | 2 | 3;

interface PerformancePlotProps {
  /** Current performance level: 1 (low) → 3 (high) */
  performance: Level;
  /** Growth potential level: 1 (low) → 3 (high) */
  potential: Level;
  /** Labels for each segment, index 0 = level 1, index 2 = level 3 */
  segmentLabels?: [string, string, string];
  /** Accent color for dot and reach area. Defaults to #534AB7 */
  accentColor?: string;
  /** Reach area color. Defaults to accentColor with opacity */
  reachColor?: string;
  width?: number;
  height?: number;
  className?: string;
  style?: React.CSSProperties;
}

const DEFAULT_LABELS: [string, string, string] = ['Emerging', 'Developing', 'Excelling'];
const DEFAULT_ACCENT = '#534AB7';

export const PerformancePlot: React.FC<PerformancePlotProps> = ({
  performance,
  potential,
  segmentLabels = DEFAULT_LABELS,
  accentColor = DEFAULT_ACCENT,
  reachColor,
  width = 180,
  height = 320,
  className,
  style,
}) => {
  const resolvedReachColor = reachColor ?? accentColor;

  // Layout constants
  const lineX = 56;
  const lineTop = 36;
  const lineBottom = height - 36;
  const lineLength = lineBottom - lineTop;
  const segmentH = lineLength / 3;

  // Tick positions (4 ticks: top + 3 dividers)
  const tickYs = [lineTop, lineTop + segmentH, lineTop + segmentH * 2, lineBottom];

  // Segment label Y positions (center of each segment)
  const segmentCenterYs = [
    lineBottom - segmentH * 0.5, // level 1 center
    lineBottom - segmentH * 1.5, // level 2 center
    lineBottom - segmentH * 2.5, // level 3 center
  ];

  // Map level 1-3 to Y coordinate
  const levelToY = (level: Level): number => {
    return lineBottom - (level - 1) * segmentH - segmentH * 0.5;
  };

  const perfY = levelToY(performance);

  // Reach area: highlight the full segment band(s) between performance and potential
  const loLevel = Math.min(performance, potential) as Level;
  const hiLevel = Math.max(performance, potential) as Level;
  const segReachTop = lineBottom - hiLevel * segmentH; // top edge of highest segment
  const segReachBottom = lineBottom - (loLevel - 1) * segmentH; // bottom edge of lowest segment
  const segReachHeight = segReachBottom - segReachTop;

  // Arrow marker id (unique per instance to avoid conflicts)
  const markerId = `arrow-${accentColor.replace('#', '')}`;
  const gradientId = `reach-gradient-${accentColor.replace('#', '')}`;

  // Gradient direction: brightest at the potential end
  // potential > performance → potential is visually higher (smaller Y) → bright at top
  // potential < performance → potential is visually lower (larger Y) → bright at bottom
  const hasGradient = performance !== potential;
  const potentialHigher = potential > performance;

  const labelX = lineX + 16;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      style={style}
      aria-label={`Performance: ${segmentLabels[performance - 1]}, Potential: ${segmentLabels[potential - 1]}`}
      role="img"
    >
      <defs>
        {hasGradient && (
          <linearGradient
            id={gradientId}
            x1="0"
            y1="0"
            x2="0"
            y2="1"
            gradientUnits="objectBoundingBox"
          >
            <stop
              offset="0%"
              stopColor={resolvedReachColor}
              stopOpacity={potentialHigher ? 0.4 : 0.05}
            />
            <stop
              offset="100%"
              stopColor={resolvedReachColor}
              stopOpacity={potentialHigher ? 0.05 : 0.4}
            />
          </linearGradient>
        )}
        <marker
          id={markerId}
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="5"
          markerHeight="5"
          orient="auto-start-reverse"
        >
          <path
            d="M2 1L8 5L2 9"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </marker>
      </defs>

      {/* Reach area — full-width segment band, drawn first (behind everything) */}
      <rect
        x={0}
        y={segReachTop}
        width={width}
        height={segReachHeight}
        fill={hasGradient ? `url(#${gradientId})` : resolvedReachColor}
        fillOpacity={hasGradient ? undefined : 0.25}
      />

      {/* Axis line */}
      <line
        x1={lineX}
        y1={lineBottom}
        x2={lineX}
        y2={lineTop - 2}
        stroke="#888780"
        strokeWidth="1.5"
        markerEnd={`url(#${markerId})`}
        strokeLinecap="round"
      />

      {/* Segment tick marks */}
      {tickYs.map((y, i) => (
        <line
          key={i}
          x1={lineX - 6}
          y1={y}
          x2={lineX + 6}
          y2={y}
          stroke="#B4B2A9"
          strokeWidth="1"
          strokeLinecap="round"
        />
      ))}

      {/* Segment labels */}
      {segmentLabels.map((label, i) => (
        <text
          key={i}
          x={labelX}
          y={segmentCenterYs[i]}
          dominantBaseline="central"
          fontSize="12"
          fontFamily="system-ui, sans-serif"
          fill={performance === ((i + 1) as Level) ? accentColor : '#888780'}
          fontWeight={performance === ((i + 1) as Level) ? '500' : '400'}
        >
          {label}
        </text>
      ))}

      {/* Performance dot */}
      <circle
        cx={lineX}
        cy={perfY}
        r="10"
        fill={accentColor}
        fillOpacity={0.15}
        stroke={accentColor}
        strokeWidth="2"
      />
      <circle cx={lineX} cy={perfY} r="4.5" fill={accentColor} />
    </svg>
  );
};

export default PerformancePlot;
